/**
 * DeleteAccountPage — public, Play-compliant delete-account flow at
 * `/delete-account`.
 *
 * Always renders the compliance disclaimer + a Delete control; renders the
 * adaptive provider-login section only when the visitor must (re-)authenticate;
 * on a confirmed delete flips into a terminal "requested / being processed"
 * success state.
 *
 * Flutter behaviour: `AppSettingsScreen._onDeleteAccount`
 * (`lib/presentation/screens/settings/app_settings_screen.dart:163-187`) — a
 * confirm sheet, then delete + sign out. The CF-call-then-sign-out ordering
 * lives in `deleteAccountUseCase` (via `useDeleteAccount`). Web deviations
 * (task-prompt-directed): the disclaimer is rendered on the page (so the
 * confirm sheet carries only a short prompt), and the post-delete step is an
 * in-page terminal success state ("requested / being processed") instead of
 * Flutter's navigate-to-login.
 *
 * Structural template: `AboutPage.tsx` — same public-route `useBasePage` +
 * `screenKey` + `useKeepAlive` wiring and centered-column layout. The
 * provider-login buttons are the shared `ProviderSignInButtons` that the login
 * screen renders too.
 *
 * App-bar provenance: the bar is the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`). `/delete-account`
 * is a **web-only compliance route** with no Flutter screen behind it — Flutter
 * runs the delete from a settings row, not a dedicated screen — so the bar has
 * no Flutter analogue and is not a parity claim; it simply follows the shared
 * page-bar shape (`lib/presentation/custom_widgets/base_app_bar.dart:5-42`:
 * back leading + `centerTitle: true` + `kToolbarHeight`) that `ExpauseAppBar`
 * ports (`workflow/lessons.md` — a full-page web route needs a back affordance
 * or a deep-linked visitor is stranded).
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured.
 *
 * Layer ownership (see `presentation.md`): the page owns all UI reactions — it
 * fires the `ConfirmSheet` and every `toast`, passing the login-outcome toast
 * handlers into `useDeleteAccount` and reacting to `confirmDelete`'s boolean.
 * The hook owns state + business logic only.
 *
 * Accessible without authentication; mounted under `PUBLIC_ROUTES` and NOT
 * wrapped in `PublicRoute` (which would redirect authenticated users away).
 */
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import type { RootState } from '@/presentation/store';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import {
  ExpauseButton,
  ButtonVariant,
  ButtonSize,
} from '@/presentation/components/common/controls/ExpauseButton';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { useDeleteAccount } from './hooks/useDeleteAccount';
import { DeleteAccountLoginSection } from './components/DeleteAccountLoginSection';

export const DeleteAccountPage = () => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const location = useLocation();

  // Used only to choose the login prompt (sign-in vs. re-auth); the gate itself
  // is owned by the hook via `showLoginButtons`. Gated on the established
  // session (the user entity), NOT raw Firebase auth — so a blocked
  // removing/suspended login (which never populates `state.auth.user`) shows
  // the sign-in prompt, not the re-auth prompt.
  const hasSession = useSelector(
    (state: RootState) => state.auth.user !== null
  );

  // The page owns every UI reaction — the hook only signals login outcomes.
  const del = useDeleteAccount({
    onRemoving: () => toast.error(l10n.auth.removingAccountMessage),
    onSuspended: () => toast.error(l10n.deleteAccount.suspendedMessage),
    onError: (message) => toast.error(message),
  });

  // Canonical screen-key wiring (mirrors `AboutPage` / `SupportPage`).
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname)
  );
  const screenKey = screenKeyRef.current;

  // No server fetch on init — the hook computes staleness on mount.
  useBasePage({ screenKey, onInit: () => {} });

  // Deep-link guard: with no in-app history, fall back to the feed
  // (same pattern as ContactPage).
  const handleBack = () => Navigator.goBackOr(() => Navigator.navigateToFeed());

  const handleDeleteClick = () => {
    ConfirmSheet.show({
      title: l10n.deleteAccount.confirmTitle,
      confirmText: l10n.deleteAccount.confirmPrompt,
      okText: l10n.deleteAccount.confirmCta,
      cancelText: l10n.common.cancel,
      screenKey,
      loadingStyle: 'button',
      onConfirm: async () => {
        const ok = await del.confirmDelete();
        if (!ok) toast.error(l10n.deleteAccount.deleteError);
      },
    });
  };

  return (
    <div
      {...qaAttr({ id: 'delete-account', status: del.isDeleted ? 'done' : 'ready' })}
      style={{
        position: 'relative',
        minHeight: '100vh',
        backgroundColor: theme.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* App bar — back button + centered title. Deliberately rendered ABOVE
          the `del.isDeleted` branch so it survives into the terminal
          success state: the back control stays useful post-delete (the visitor
          is signed out, and the history/Feed fallback still applies). */}
      <ExpauseAppBar
        rootQa={{ id: 'delete-account-app-bar' }}
        title={l10n.deleteAccount.pageTitle}
        leadingQa={{ id: 'delete-account-back' }}
        onLeadingTap={handleBack}
      />

      {/* Body — centered max-width column under the app bar. */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: `${responsive.spacing.lg}px`,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: responsive.dialog.maxWidth,
            display: 'flex',
            flexDirection: 'column',
            gap: responsive.spacing.lg,
          }}
        >
          {del.isDeleted ? (
            // Terminal success state — replaces the disclaimer/controls. No
            // further action; the visitor is signed out and the page is public.
            <div
              {...qaAttr({ id: 'delete-account-success', status: 'done' })}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: responsive.spacing.base,
              }}
            >
              <h1
                style={{
                  fontSize: responsive.font['2xl'],
                  fontWeight: responsive.fontWeight.semibold,
                  color: theme.text.primary,
                  margin: 0,
                }}
              >
                {l10n.deleteAccount.successTitle}
              </h1>
              <p
                style={{
                  fontSize: responsive.font.base,
                  color: theme.text.secondary,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                {l10n.deleteAccount.successMessage}
              </p>
            </div>
          ) : (
            <>
              <p
                style={{
                  fontSize: responsive.font.base,
                  color: theme.text.secondary,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                {l10n.deleteAccount.disclaimerParagraph1}
              </p>
              <p
                style={{
                  fontSize: responsive.font.base,
                  color: theme.text.secondary,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                {l10n.deleteAccount.disclaimerParagraph2}
              </p>

              {/* Destructive delete control. Disabled-but-visible when login is
                  required (intentional UI structure, not an omission). */}
              <ExpauseButton
                {...qaAttr({ id: 'delete-account-submit' })}
                variant={ButtonVariant.danger}
                size={ButtonSize.large}
                onClick={handleDeleteClick}
                disabled={del.showLoginButtons || del.isDeleting}
                loading={del.isDeleting}
                className="w-full max-w-sm self-center"
              >
                {l10n.deleteAccount.deleteButton}
              </ExpauseButton>

              {del.showLoginButtons && (
                <DeleteAccountLoginSection
                  hasSession={hasSession}
                  isLoggingIn={del.isLoggingIn}
                  onLoginWithGoogle={del.onLoginWithGoogle}
                  onLoginWithApple={del.onLoginWithApple}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Full-screen loading overlay while a sign-in flow runs — same shape and
          shared tokens as LoginPage's (Flutter `savingOverlayWidget()`,
          `login_screen.dart:497-512`): a full-viewport backdrop blur at
          `responsive.auth.overlayBlur` (= Flutter's
          `DimensionConstants.bottomSheetBlurSigmaX/Y` = 5) centring a bare,
          default-sized `ExpauseLoadingIndicator`. Tokens are shared with
          LoginPage on purpose so the two mirrored overlays cannot drift. */}
      {del.isLoggingIn && (
        <div
          {...qaAttr({ id: 'delete-account-loading', status: 'loading' })}
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{
            backgroundColor: theme.loadingOverlay,
            backdropFilter: `blur(${responsive.auth.overlayBlur}px)`,
            WebkitBackdropFilter: `blur(${responsive.auth.overlayBlur}px)`,
          }}
        >
          <ExpauseLoadingIndicator />
        </div>
      )}
    </div>
  );
};
